import { AfterViewInit, Component, computed, Signal, Input } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import {
  DocumentRequestPayload,
  DocumentRequirements,
  RemainingDocumentRequirement,
  DocumentSource
} from 'app/models/document-requirement/document-requirement.model';
import { DealDocumentState } from 'app/store/deal/deal-documents/deal-document.state';
import { Store } from '@ngxs/store';
import * as DA from 'app/store/deal/deal-documents/deal-document.actions';
import { DocumentRequirementDialogComponent } from './document-requirement-dialog/document-requirement-dialog.component';
import { CommonModule } from '@angular/common';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { Deal } from 'app/models/dto/deal.model';
import { formatDocumentRequestsPayload } from './document-requirements-helpers';
import { DeleteDocumentRequirementDialogComponent } from './document-requirement-dialog/document-requirement-delete-dialog.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconButton } from '@angular/material/button';
import { ProcessTimestampsTooltipComponent } from 'app/components/process-timestamps-tooltip/process-timestamps-tooltip.component';
import { Overlay, OverlayModule, ScrollStrategy } from '@angular/cdk/overlay';
import { trigger, transition, style, animate } from '@angular/animations';

@Component({
  selector: 'app-document-requirements',
  standalone: true,
  imports: [
    MatProgressSpinnerModule,
    CommonModule,
    MatCardModule,
    MatExpansionModule,
    MatIconModule,
    MatIconButton,
    MatTooltipModule,
    ProcessTimestampsTooltipComponent,
    OverlayModule,
  ],
  templateUrl: './document-requirements.component.html',
  styleUrl: './document-requirements.component.scss',
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-in', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ opacity: 0 }))
      ])
    ])
  ]
})
export class DocumentRequirementsComponent implements AfterViewInit {
  @Input() deal: Deal;
  DocumentSource = DocumentSource;
  documentRequirements: Signal<(dealId: number) => DocumentRequirements>;
  uploadedButNotAccepted: Signal<RemainingDocumentRequirement[]>;
  notUploaded: Signal<RemainingDocumentRequirement[]>;
  canAddDocumentRequirements: Signal<boolean>;
  acceptedFraction: Signal<string>;
  acceptedPercentage: Signal<string>;
  hasRequirements: Signal<boolean>;
  hasEsignaturePlus: Signal<boolean>;
  showDocusignTimestamps: string | null = null;
  openDocumentRequirementListItemRef: HTMLElement | null = null;
  openESignaturePopoverRef : HTMLElement | null = null;
  scrollStrategy: ScrollStrategy | null = null;
  dealLoadingRequirements: Signal<(dealId: number) => boolean>;
  loadingRequirements: Signal<boolean>;
  initialLoading: boolean = true;
  
  constructor(
    private dialog: MatDialog,
    private _store: Store,
    private overlay: Overlay,
  ) {
    this.documentRequirements = this._store.selectSignal(DealDocumentState.advancedDealDocumentRequirements);
    this.hasEsignaturePlus = this._store.selectSignal<boolean>(LenderUserState.hasPermission('eaSignaturePlus'));
    this.dealLoadingRequirements = this._store.selectSignal(DealDocumentState.dealLoadingRequirements);

    this.hasRequirements = computed(() => {
      if (!this.deal?.id) return false;
      const req = this.documentRequirements()(this.deal.id);
      return (
        req.satisfied.length > 0 ||
        req.remaining.length > 0
      );
    });

    this.loadingRequirements = computed(() => {
      if (!this.deal?.id) return true;
      if (this.dealLoadingRequirements()(this.deal.id)) {
        return true;
      }
      this.initialLoading = false;
      return false;
    });

    this.acceptedFraction = computed(() => {
      if (!this.deal?.id || !this.hasRequirements()) {
        return 'No requirements available';
      }

      const requirements = this.documentRequirements()(this.deal.id);
      if (!requirements || !('satisfied' in requirements)) {
        return 'No requirements available';
      }

      return `${requirements.satisfied.length}/${requirements.satisfied.length +
        requirements.remaining.length
        } Ready`;
    });

    this.acceptedPercentage = computed(() => {
      if (!this.deal?.id || !this.hasRequirements()) {
        return '';
      }

      const requirements = this.documentRequirements()(this.deal.id);
      if (!requirements || !('satisfied' in requirements)) {
        return '';
      }

      const percentage = (requirements.satisfied.length / (requirements.remaining.length + requirements.satisfied.length)) * 100;
      return `${percentage.toFixed(0)}% Complete`;
    });


    this.notUploaded = computed(() => {
      if (!this.deal?.id) return [];
      const requirements = this.documentRequirements()(this.deal.id);
      return requirements && 'remaining' in requirements ? requirements.remaining.filter((dr) => !dr.isSatisfied) : [];
    });

    this.uploadedButNotAccepted = computed(() => {
      if (!this.deal?.id) return [];
      const requirements = this.documentRequirements()(this.deal.id);
      return requirements && 'remaining' in requirements ? requirements.remaining.filter((dr) => dr.isSatisfied) : [];
    });
  }

  ngAfterViewInit(): void {
    this.scrollStrategy = this.overlay.scrollStrategies.close();
  }

  openDocumentRequirementDialog(event): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(DocumentRequirementDialogComponent, {
      position: { top: '10%' },
      width: '408px',
      disableClose: true,
      data: { dealId: this.deal.id }
    });

    dialogRef.componentInstance.handleSave.subscribe({
      next: (payload: DocumentRequestPayload) => {
        const dealId = this.deal.id;
        const documentRequestsPayload = formatDocumentRequestsPayload(payload);
        this._store.dispatch(new DA.PostDocumentRequirements(dealId, documentRequestsPayload));
      }
    });
  }

  handleRequirementDelete(event, requiredDocument): void {
    event.stopPropagation();
    this.dialog.open(DeleteDocumentRequirementDialogComponent, {
      width: '375px',
      data: { requiredDocument, dealId: this.deal.id }
    });
  }

  handleEsignatureClick(event, requiredDocument): void {
    event.stopPropagation();
    const origin = event.target as HTMLElement;
    if (this.showDocusignTimestamps) {
      this.showDocusignTimestamps = null;
      this.openESignaturePopoverRef  = null;
      return;
    }
    this.openESignaturePopoverRef = origin;
    const parent = this.openESignaturePopoverRef.closest('.document-requirement') as HTMLElement;
    this.openDocumentRequirementListItemRef = parent;
    this.showDocusignTimestamps = requiredDocument?.documentCategory?.name;
  }

  handleOverlayOutsideClick(): void {
      this.openDocumentRequirementListItemRef?.classList.remove('hover-effect');
      this.showDocusignTimestamps = null;
      this.openDocumentRequirementListItemRef = null;
      this.openESignaturePopoverRef = null;
  }

  handleOverlayClose(): void {
    this.showDocusignTimestamps = null;
    this.openDocumentRequirementListItemRef = null;
    this.openESignaturePopoverRef = null;
  }
}


